import { FetchError } from "@decibeltrade/sdk";
import z from "zod/v4";

/**
 * Recover the server's `{ success: false, error, code }` body from a thrown request error.
 *
 * Needed because `baseRequest` in the SDK throws `FetchError` on any non-2xx *before* it parses
 * the body against the response schema (`packages/sdk/src/utils.ts:179`). Every xchain rejection
 * is a 4xx or 5xx, so the failure branch of a discriminated-union response schema is unreachable:
 * without this, a bad Bitcoin address and an unreachable bridge are the same opaque
 * `HTTP Error 400` and the modal can say nothing more useful than "try again".
 *
 * `code` is returned as a plain string for the caller to narrow against its own enum — each
 * endpoint accepts a different subset, and a code this build has not heard of should degrade to
 * "no code" rather than discard the message alongside it.
 */

const XchainFailureSchema = z.object({
  success: z.literal(false),
  error: z.string(),
  code: z.string().optional(),
});

export interface XchainFailure {
  error: string;
  code: string | undefined;
}

export function xchainFailure(error: unknown): XchainFailure | undefined {
  if (!(error instanceof FetchError)) return undefined;

  // `responseMessage` is the raw body unless it happens to carry a `message` key, which is how
  // the auth middleware and Hono's own validation errors answer. Those are not ours, and the
  // parse below is what tells them apart.
  let body: unknown;
  try {
    body = JSON.parse(error.responseMessage);
  } catch {
    return undefined;
  }

  const parsed = XchainFailureSchema.safeParse(body);
  if (!parsed.success) return undefined;

  return { error: parsed.data.error, code: parsed.data.code };
}
